import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Table, Button, Empty, Spin, Space, Tag, Modal, Banner } from '@douyinfe/semi-ui';
import { IconUserAdd, IconGift, IconCopy, IconShareStroked, IconShare } from '@douyinfe/semi-icons';
import { API, showSuccess, showError, copy } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import QRCode from 'qrcode.react';

const { Title, Text } = Typography;

const Referral = () => {
  const [stats, setStats] = useState({
    total_invited: 0,
    total_earnings: 0,
    pending_earnings: 0,
    total_topup: 0,
    charged_count: 0,
  });
  const [records, setRecords] = useState([]);
  const [invitees, setInvitees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      // 获取邀请码 - 使用 /api/user/aff 接口
      const affRes = await API.get('/api/user/aff').catch(() => ({ data: { success: false } }));
      if (affRes.data.success) {
        const code = affRes.data.data;
        setReferralCode(code);
        setReferralLink(`${window.location.origin}/register?aff=${code}`);
      }

      const [statsRes, recordsRes, inviteesRes] = await Promise.all([
        API.get('/api/referral/stats').catch(() => ({ data: { success: false } })),
        API.get('/api/referral/records').catch(() => ({ data: { success: false } })),
        API.get('/api/referral/invitees').catch(() => ({ data: { success: false } })),
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data || { total_invited: 0, total_earnings: 0, pending_earnings: 0, total_topup: 0, charged_count: 0 });
      }
      if (recordsRes.data.success) {
        const recordsData = recordsRes.data.data || [];
        console.log('=== Referral Records Debug ===');
        console.log('Raw data:', recordsData);
        if (recordsData.length > 0) {
          console.log('First record:', recordsData[0]);
          console.log('First record keys:', Object.keys(recordsData[0]));
          console.log('invitee_name:', recordsData[0].invitee_name);
          console.log('amount:', recordsData[0].amount);
        }
        setRecords(recordsData);
      }
      if (inviteesRes.data.success) {
        const inviteesData = inviteesRes.data.data || [];
        console.log('=== Invitees Debug ===');
        console.log('Invitees data:', inviteesData);
        if (inviteesData.length > 0) {
          console.log('First invitee:', inviteesData[0]);
          console.log('registered_at:', inviteesData[0].registered_at);
          console.log('registered_at type:', typeof inviteesData[0].registered_at);
        }
        setInvitees(inviteesData);
      }
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = async () => {
    if (!referralLink) {
      showError('邀请链接加载中，请稍后再试');
      return;
    }
    if (await copy(referralLink)) {
      showSuccess('邀请链接已复制到剪贴板');
    } else {
      showError('复制失败，请手动复制');
    }
  };

  const shareToWechat = () => {
    setShowQRModal(true);
  };

  const shareToTwitter = () => {
    const text = `邀请你使用 AI API 网关，注册即送额度！`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`;
    window.open(url, '_blank');
  };

  // 自定义微信图标组件
  const WechatIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
    </svg>
  );

  // 自定义 Twitter 图标组件
  const TwitterIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  const recordColumns = [
    { 
      title: '时间', 
      dataIndex: 'created_at', 
      key: 'created_at', 
      render: (text) => new Date(text).toLocaleString() 
    },
    { 
      title: '被邀请人', 
      dataIndex: 'invitee_name', 
      key: 'invitee_name', 
      render: (text) => text || '-' 
    },
    { 
      title: '类型', 
      dataIndex: 'type', 
      key: 'type', 
      render: (text) => {
        const typeMap = {
          'register': '注册奖励（被邀请者）',
          'register_reward': '注册奖励（邀请者）',
          'topup': '充值返利',
          'refund': '退款'
        };
        const colorMap = {
          'register': 'blue',
          'register_reward': 'green',
          'topup': 'orange',
          'refund': 'red'
        };
        return <Tag color={colorMap[text] || 'default'}>{typeMap[text] || text}</Tag>;
      }
    },
    { 
      title: '奖励额度', 
      dataIndex: 'amount', 
      key: 'amount', 
      render: (text) => {
        const quota = text || 0;
        return <span style={{ color: '#10B981', fontWeight: 'bold' }}>{renderQuota(quota)}</span>;
      }
    },
    { 
      title: '被邀请人充值', 
      dataIndex: 'invitee_quota', 
      key: 'invitee_quota',
      render: (text) => {
        const quota = text || 0;
        return quota > 0 ? renderQuota(quota) : '-';
      }
    },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status', 
      render: (text) => {
        const status = text === 1 ? 'completed' : 'pending';
        return <Tag color={status === 'completed' ? 'green' : 'orange'}>
          {status === 'completed' ? '✓ 已结算' : '⏳ 待结算'}
        </Tag>;
      }
    },
  ];

  const inviteeColumns = [
    { 
      title: '用户名', 
      dataIndex: 'username', 
      key: 'username' 
    },
    { 
      title: '邮箱', 
      dataIndex: 'email', 
      key: 'email',
      render: (text) => text || '未绑定'
    },
    { 
      title: '注册时间', 
      dataIndex: 'registered_at', 
      key: 'registered_at', 
      render: (text) => {
        if (!text) return '-';
        try {
          const date = new Date(text);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          return '-';
        }
      }
    },
    { 
      title: '充值额度', 
      dataIndex: 'total_topup', 
      key: 'total_topup', 
      render: (text) => {
        const quota = text || 0;
        return <span style={{ fontWeight: 'bold', color: '#667eea' }}>{renderQuota(quota)}</span>;
      }
    },
    { 
      title: '状态', 
      dataIndex: 'status', 
      key: 'status',
      render: (text) => {
        return <Tag color={text === 1 ? 'green' : 'red'}>
          {text === 1 ? '正常' : '禁用'}
        </Tag>;
      }
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 60px)' }}>
      {/* 页面标题区域 - 参考首页风格 */}
      <div style={{ 
        marginBottom: '24px',
        padding: '24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: '#fff'
      }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
          🎁 邀请奖励
        </h2>
        <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
          邀请好友注册并充值，即可获得丰厚奖励
        </p>
      </div>

      {/* 推广横幅 - 优化样式 */}
      <Card
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          marginBottom: 24,
          borderRadius: '8px',
          border: 'none',
          boxShadow: '0 4px 12px rgba(fff, fff, fff, 0.3)'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', color: '#fff', fontSize: '20px', fontWeight: '600' }}>
              <IconGift style={{ marginRight: 8 }} /> 邀请好友，获取奖励
            </h3>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
              每邀请一位好友注册，双方都能获得奖励！
            </span>
          </div>
          <Space>
            <Button 
              size="large" 
              style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: '#fff', 
                fontWeight: 'bold', 
                borderRadius: '8px', 
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
              }} 
              onClick={copyInviteLink} 
              icon={<IconCopy />}
            >
              复制邀请链接
            </Button>
            <Button 
              size="large" 
              style={{ 
                background: 'rgba(255,255,255,0.2)', 
                color: '#fff', 
                border: '1px solid rgba(255,255,255,0.5)', 
                borderRadius: '8px' 
              }} 
              onClick={() => setShowQRModal(true)} 
              icon={<IconShareStroked />}
            >
              分享二维码
            </Button>
          </Space>
        </div>
      </Card>

      {/* 邀请链接卡片 - 优化样式 */}
      <Card style={{ 
        marginBottom: 24, 
        borderRadius: '8px',
        border: '1px solid #e4e7ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ padding: '20px' }}>
          <Title heading={5} style={{ marginBottom: 16 }}>我的邀请链接</Title>
          <div style={{
            background: '#f5f7fa',
            padding: 16,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 12,
            border: '1px solid #e4e7ed'
          }}>
            <code style={{ fontSize: 14, color: '#667eea', wordBreak: 'break-all', flex: 1, fontFamily: 'monospace' }}>
              {referralLink || '加载中...'}
            </code>
            <Button
              type="primary"
              theme="solid"
              onClick={copyInviteLink}
              disabled={!referralLink}
              icon={<IconCopy />}
              style={{ borderRadius: '8px' }}
            >
              复制链接
            </Button>
          </div>

          {/* 分享按钮 - 优化样式 */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button
              onClick={shareToWechat}
              style={{
                background: '#07C160',
                color: 'white',
                border: 'none',
                borderRadius: '8px'
              }}
              icon={<WechatIcon />}
            >
              分享到微信
            </Button>
            <Button
              onClick={shareToTwitter}
              style={{
                background: '#1DA1F2',
                color: 'white',
                border: 'none',
                borderRadius: '8px'
              }}
              icon={<TwitterIcon />}
            >
              分享到 Twitter
            </Button>
          </div>
        </div>
      </Card>

      {/* 奖励规则 - 优化样式 */}
      <Card style={{ 
        marginBottom: 24, 
        borderRadius: '8px',
        border: '1px solid #e4e7ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ padding: '20px' }}>
          <Title heading={5} style={{ marginBottom: 20 }}>奖励规则</Title>
          <Space vertical spacing="loose" style={{ width: '100%' }}>
            <Banner
              type="info"
              title="邀请者奖励"
              description="每邀请一位新用户注册，您将获得奖励（奖励额度由管理员在系统设置中配置，立即结算）"
              closeIcon={null}
              style={{ borderRadius: '8px' }}
            />
            <Banner
              type="success"
              title="被邀请者奖励"
              description="通过邀请链接注册的新用户，将获得额外奖励额度（奖励额度由管理员在系统设置中配置，在新用户初始额度基础上额外增加，立即结算）"
              closeIcon={null}
              style={{ borderRadius: '8px' }}
            />
            <Banner
              type="warning"
              title="充值返利"
              description="被邀请用户每次充值，邀请者将获得 10% 的返利（立即结算）"
              closeIcon={null}
              style={{ borderRadius: '8px' }}
            />
          </Space>
          <div style={{ marginTop: 16, padding: 12, background: '#f5f7fa', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              💡 <strong>提示：</strong>邀请者奖励和被邀请者奖励的具体额度由管理员在「系统设置 → 运营设置 → 额度设置」中配置，配置后立即生效。
            </Text>
          </div>
        </div>
      </Card>

      {/* 统计卡片 - 优化样式 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card style={{ 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #fff 100%)'
          }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                margin: '0 auto',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IconUserAdd style={{ color: '#fff', fontSize: 28 }} />
              </div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#303133', marginBottom: 8 }}>{stats.total_invited}</div>
            <Text type="tertiary" style={{ fontSize: '14px' }}>邀请人数</Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card style={{ 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #fff 100%)'
          }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                margin: '0 auto',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                color: '#fff',
                fontWeight: 'bold'
              }}>¥</div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#303133', marginBottom: 8 }}>
              {renderQuota(stats.total_earnings)}
            </div>
            <Text type="tertiary" style={{ fontSize: '14px' }}>累计奖励</Text>
          </Card>
        </Col>
        <Col span={8}>
          <Card style={{ 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #fff 100%)'
          }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                margin: '0 auto',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                color: '#fff',
                fontWeight: 'bold'
              }}>¥</div>
            </div>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: '#303133', marginBottom: 8 }}>
              {renderQuota(stats.pending_earnings)}
            </div>
            <Text type="tertiary" style={{ fontSize: '14px' }}>待发放奖励</Text>
          </Card>
        </Col>
      </Row>

      {/* 邀请记录 - 优化样式 */}
      <Card style={{ 
        marginBottom: 24, 
        borderRadius: '8px',
        border: '1px solid #e4e7ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ padding: '20px' }}>
          <Title heading={5} style={{ marginBottom: 16 }}>奖励记录</Title>
          {records.length === 0 ? (
            <Empty description="暂无奖励记录" style={{ padding: 20 }} />
          ) : (
            <Table columns={recordColumns} dataSource={records} pagination={{ pageSize: 10 }} />
          )}
        </div>
      </Card>

      {/* 邀请用户 - 优化样式 */}
      <Card style={{ 
        borderRadius: '8px',
        border: '1px solid #e4e7ed',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ padding: '20px' }}>
          <Title heading={5} style={{ marginBottom: 16 }}>已邀请用户</Title>
          {invitees.length === 0 ? (
            <Empty description="暂无邀请用户" style={{ padding: 20 }} />
          ) : (
            <Table columns={inviteeColumns} dataSource={invitees} pagination={{ pageSize: 10 }} />
          )}
        </div>
      </Card>

      {/* 二维码分享弹窗 - 优化样式 */}
      <Modal
        title="扫描二维码分享"
        visible={showQRModal}
        onCancel={() => setShowQRModal(false)}
        footer={null}
        centered
        width={360}
      >
        <div style={{ textAlign: 'center', padding: 20 }}>
          {referralLink ? (
            <>
              <QRCode value={referralLink} size={200} />
              <p style={{ marginTop: 16, color: '#606266' }}>
                扫描二维码注册
              </p>
              <div style={{ marginTop: 16 }}>
                <Text type="tertiary" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {referralLink}
                </Text>
              </div>
            </>
          ) : (
            <Spin size="large" />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Referral;
